import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  Calendar,
  TrendingUp,
  Database,
  X,
  MessageSquare,
  FileText,
  Image,
  ChevronRight,
} from 'lucide-react';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Card } from '@/components/ui/Card/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableColumn,
  TableCell,
} from '@/components/ui/Table';
import { Skeleton } from '@/components/ui';
import { Paginator } from '@/components/ui/Paginator/Paginator';
import { Select } from '@/components/forms';
import { useUsers } from '@/api/useUsers';
import { formatCurrency } from '@/utils/format';
import styles from './TokenUsage.module.css';

// ─── Types ──────────────────────────────────────────────────────────────────

interface TokenUsageRecord {
  id: string;
  created: string;
  document_name: string | null;
  source: 'DOCUMENT' | 'CHAT' | 'IMAGE';
  chat_session: string | null;
  user_email?: string | null;
  claude_input_tokens: number;
  claude_output_tokens: number;
  claude_cost: string;
  gemini_input_tokens: number;
  gemini_output_tokens: number;
  gemini_cost: string;
  total_tokens: number;
  total_cost: string;
  model_used: string;
  providers_created: number;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  results: T[];
}

interface StatsData {
  total_cost: number | null;
  total_tokens: number | null;
  claude_cost: number | null;
  claude_tokens: number | null;
  gemini_cost: number | null;
  gemini_tokens: number | null;
  count: number;
}

interface UsageStats {
  total: StatsData;
  today: StatsData;
  week: StatsData;
  month: StatsData;
  last_hour: StatsData;
}

// ─── Panel config ────────────────────────────────────────────────────────────

type PanelId = 'last_hour' | 'today' | 'month' | 'total' | 'all';

interface PanelConfig {
  id: PanelId;
  title: string;
  filter: string;
}

type SourceFilter = '' | 'DOCUMENT' | 'CHAT' | 'IMAGE';
const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: '', label: 'All Sources' },
  { value: 'CHAT', label: 'Chat' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'IMAGE', label: 'Image' },
];

const PANEL_CONFIGS: Record<PanelId, PanelConfig> = {
  last_hour: { id: 'last_hour', title: 'Last Hour Usage', filter: '' },
  today: { id: 'today', title: "Today's Usage", filter: '' },
  month: { id: 'month', title: 'This Month Usage', filter: '' },
  total: { id: 'total', title: 'All Time Usage', filter: '' },
  all: { id: 'all', title: 'All Activity', filter: '' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function costBarWidths(stat: StatsData | undefined): { claude: number; gemini: number } {
  const c = stat?.claude_cost ?? 0;
  const g = stat?.gemini_cost ?? 0;
  const total = c + g;
  if (total === 0) return { claude: 0, gemini: 0 };
  return { claude: (c / total) * 100, gemini: (g / total) * 100 };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface SourceBadgeProps {
  source: TokenUsageRecord['source'];
}

function SourceBadge({ source }: SourceBadgeProps) {
  if (source === 'CHAT') {
    return (
      <span className={`${styles.badge} ${styles.badge_chat}`}>
        <MessageSquare size={10} />
        Chat
      </span>
    );
  }
  if (source === 'IMAGE') {
    return (
      <span className={`${styles.badge} ${styles.badge_image}`}>
        <Image size={10} />
        Image
      </span>
    );
  }
  return (
    <span className={`${styles.badge} ${styles.badge_doc}`}>
      <FileText size={10} />
      Doc
    </span>
  );
}

interface DetailTableProps {
  panelId: PanelId;
  userEmail?: string;
  sourceFilter?: SourceFilter;
}

function DetailTable({ panelId, userEmail, sourceFilter }: DetailTableProps) {
  const [page, setPage] = useState(1);

  const { data: logs, isLoading } = useQuery<PaginatedResponse<TokenUsageRecord>>({
    queryKey: ['token-usage-panel-logs', panelId, page, userEmail, sourceFilter],
    queryFn: async () => {
      let url = `${API_ROUTES.tokenUsage}?page=${page}&page_size=8`;
      if (userEmail) url += `&user__email__icontains=${encodeURIComponent(userEmail)}`;
      if (sourceFilter) url += `&source=${sourceFilter}`;
      const res = await api.get(url);
      return res.data;
    },
  });

  const skeletonRows = Array.from({ length: 5 }).map((_, i) => (
    <TableRow key={`sk-${i}`}>
      <TableCell>
        <Skeleton width="90px" />
      </TableCell>
      <TableCell>
        <Skeleton width="55px" />
      </TableCell>
      <TableCell>
        <Skeleton width="120px" />
      </TableCell>
      <TableCell>
        <Skeleton width="140px" />
      </TableCell>
      <TableCell>
        <Skeleton width="80px" />
      </TableCell>
      <TableCell>
        <Skeleton width="60px" />
      </TableCell>
      <TableCell>
        <Skeleton width="55px" />
      </TableCell>
      <TableCell>
        <Skeleton width="30px" />
      </TableCell>
    </TableRow>
  ));

  return (
    <div className={styles.detail_table_wrap}>
      <div className={styles.detail_table_scroll}>
        <Table>
          <TableHeader>
            <TableColumn>Date</TableColumn>
            <TableColumn>Source</TableColumn>
            <TableColumn>User</TableColumn>
            <TableColumn>Details</TableColumn>
            <TableColumn>Tokens In/Out</TableColumn>
            <TableColumn>Total Tokens</TableColumn>
            <TableColumn>Cost</TableColumn>
            <TableColumn>Gen.</TableColumn>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              skeletonRows
            ) : !logs?.results.length ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={styles.empty}>No records found.</div>
                </TableCell>
              </TableRow>
            ) : (
              logs.results.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <span className={styles.cell_mono}>{formatDate(log.created)}</span>
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={log.source} />
                  </TableCell>
                  <TableCell>
                    <span className={styles.cell_muted} title={log.user_email ?? ''}>
                      {log.user_email ?? <em>system</em>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={styles.detail_cell}>
                      <span title={log.document_name || `Session ${log.chat_session}`}>
                        {log.source === 'CHAT'
                          ? `Session #${log.chat_session}`
                          : log.document_name || 'Generated Image'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.token_stack}>
                      {log.claude_input_tokens + log.claude_output_tokens > 0 && (
                        <span>
                          {log.claude_input_tokens.toLocaleString()} /{' '}
                          {log.claude_output_tokens.toLocaleString()}
                        </span>
                      )}
                      {log.gemini_input_tokens + log.gemini_output_tokens > 0 && (
                        <span className={styles.gemini_tint}>
                          {log.gemini_input_tokens.toLocaleString()} /{' '}
                          {log.gemini_output_tokens.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={styles.pill_neutral}>{log.total_tokens.toLocaleString()}</span>
                  </TableCell>
                  <TableCell>
                    <span className={styles.pill_cost}>
                      {formatCurrency(parseFloat(log.total_cost), 'USD')}
                    </span>
                  </TableCell>
                  <TableCell>{log.providers_created || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {logs && logs.total_pages > 1 && (
        <div className={styles.panel_paginator}>
          <Paginator page={page} numPages={logs.total_pages} onPageChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
}

// ─── Slide-over panel ────────────────────────────────────────────────────────

interface SlideOverProps {
  panelId: PanelId | null;
  onClose: () => void;
  userEmail?: string;
  sourceFilter?: SourceFilter;
}

function SlideOver({ panelId, onClose, userEmail, sourceFilter }: SlideOverProps) {
  const isOpen = panelId !== null;
  const config = panelId ? PANEL_CONFIGS[panelId] : null;

  return (
    <>
      <div
        className={`${styles.backdrop} ${isOpen ? styles.backdrop_open : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.slideover} ${isOpen ? styles.slideover_open : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.slideover_header}>
          <h2 className={styles.slideover_title}>{config?.title ?? ''}</h2>
          <button className={styles.close_btn} onClick={onClose} aria-label="Close panel">
            <X size={16} />
          </button>
        </div>
        <div className={styles.slideover_body}>
          {panelId && (
            <DetailTable panelId={panelId} userEmail={userEmail} sourceFilter={sourceFilter} />
          )}
        </div>
      </aside>
    </>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  stat: StatsData | undefined;
  isLoading: boolean;
  icon: React.ReactNode;
  accentClass: string;
  onClick: () => void;
}

function StatCard({ label, stat, isLoading, icon, accentClass, onClick }: StatCardProps) {
  const bars = costBarWidths(stat);

  return (
    <Card className={`${styles.stat_card}`} onClick={onClick} hoverable>
      <div className={styles.stat_top}>
        <div className={`${styles.stat_icon} ${accentClass}`}>{icon}</div>
        <ChevronRight size={13} className={styles.stat_chevron} />
      </div>
      <div className={styles.stat_label}>{label}</div>
      <div className={styles.stat_value}>
        {isLoading ? (
          <Skeleton width="80px" height="24px" />
        ) : (
          formatCurrency(stat?.total_cost ?? 0, 'USD')
        )}
      </div>
      <div className={styles.stat_meta}>
        {isLoading ? (
          <Skeleton width="60px" height="12px" />
        ) : (
          <span>{stat?.count ?? 0} calls</span>
        )}
      </div>
      <div className={styles.bar_track}>
        {isLoading ? (
          <Skeleton width="100%" height="4px" />
        ) : (
          <>
            <div
              className={`${styles.bar_segment} ${styles.bar_claude}`}
              style={{ width: `${bars.claude}%` }}
              title={`Claude: ${formatCurrency(stat?.claude_cost ?? 0, 'USD')}`}
            />
            <div
              className={`${styles.bar_segment} ${styles.bar_gemini}`}
              style={{ width: `${bars.gemini}%` }}
              title={`Gemini: ${formatCurrency(stat?.gemini_cost ?? 0, 'USD')}`}
            />
          </>
        )}
      </div>
      <div className={styles.bar_legend}>
        <span className={styles.legend_claude}>
          <span className={styles.legend_dot} />
          Claude {isLoading ? '' : formatCurrency(stat?.claude_cost ?? 0, 'USD')}
        </span>
        <span className={styles.legend_gemini}>
          <span className={styles.legend_dot} />
          Gemini {isLoading ? '' : formatCurrency(stat?.gemini_cost ?? 0, 'USD')}
        </span>
      </div>
    </Card>
  );
}

// ─── Recent activity row ──────────────────────────────────────────────────────

interface ActivityRowProps {
  log: TokenUsageRecord;
}

function ActivityRow({ log }: ActivityRowProps) {
  const userLabel = log.user_email ? log.user_email.split('@')[0] : 'system';
  return (
    <div className={styles.activity_row}>
      <span className={styles.activity_time}>{timeAgo(log.created)}</span>
      <SourceBadge source={log.source} />
      <span className={styles.activity_user} title={log.user_email ?? 'system'}>
        {userLabel}
      </span>
      <span className={styles.activity_detail}>
        {log.source === 'CHAT'
          ? `#${log.chat_session}`
          : log.document_name
            ? log.document_name.length > 18
              ? log.document_name.slice(0, 18) + '…'
              : log.document_name
            : 'Image'}
      </span>
      <span className={styles.activity_cost}>
        {formatCurrency(parseFloat(log.total_cost), 'USD')}
      </span>
    </div>
  );
}

// ─── Cost breakdown chart ─────────────────────────────────────────────────────

interface BreakdownChartProps {
  stats: UsageStats | undefined;
  isLoading: boolean;
  onBarClick: (panelId: PanelId) => void;
}

function BreakdownChart({ stats, isLoading, onBarClick }: BreakdownChartProps) {
  const rows: Array<{ label: string; key: keyof UsageStats; panelId: PanelId }> = [
    { label: 'Last Hour', key: 'last_hour', panelId: 'last_hour' },
    { label: 'Today', key: 'today', panelId: 'today' },
    { label: 'Month', key: 'month', panelId: 'month' },
    { label: 'All Time', key: 'total', panelId: 'total' },
  ];

  const maxCost = stats ? Math.max(...rows.map((r) => stats[r.key]?.total_cost ?? 0)) : 1;

  return (
    <div className={styles.chart_list}>
      {rows.map(({ label, key, panelId }) => {
        const stat = stats?.[key];
        const total = stat?.total_cost ?? 0;
        const claude = stat?.claude_cost ?? 0;
        const gemini = stat?.gemini_cost ?? 0;
        const claudePct = maxCost > 0 ? (claude / maxCost) * 100 : 0;
        const geminiPct = maxCost > 0 ? (gemini / maxCost) * 100 : 0;

        return (
          <button
            key={key}
            className={styles.chart_row}
            onClick={() => onBarClick(panelId)}
            title={`View ${label} details`}
          >
            <span className={styles.chart_label}>{label}</span>
            <div className={styles.chart_bar_wrap}>
              {isLoading ? (
                <Skeleton width="100%" height="10px" />
              ) : (
                <div className={styles.chart_bar_track}>
                  <div
                    className={`${styles.chart_bar_fill} ${styles.bar_claude}`}
                    style={{ width: `${claudePct}%` }}
                  />
                  <div
                    className={`${styles.chart_bar_fill} ${styles.bar_gemini}`}
                    style={{ width: `${geminiPct}%` }}
                  />
                </div>
              )}
            </div>
            <span className={styles.chart_cost}>
              {isLoading ? <Skeleton width="50px" /> : formatCurrency(total, 'USD')}
            </span>
            <ChevronRight size={12} className={styles.chart_chevron} />
          </button>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TokenUsage() {
  const [openPanel, setOpenPanel] = useState<PanelId | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('');

  const { data: users } = useUsers();
  const userOptions = [
    { value: '', label: 'All Users' },
    ...(users ?? []).map((u) => ({ value: u.email, label: u.email })),
  ];

  const {
    data: stats,
    isLoading: isLoadingStats,
    isError: isStatsError,
  } = useQuery<UsageStats>({
    queryKey: ['token-usage-stats', userEmail, sourceFilter],
    queryFn: async () => {
      let url = `${API_ROUTES.tokenUsage}stats/`;
      const params: string[] = [];
      if (userEmail) params.push(`user__email=${encodeURIComponent(userEmail)}`);
      if (sourceFilter) params.push(`source=${sourceFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await api.get(url);
      return res.data;
    },
  });

  const {
    data: recentLogs,
    isLoading: isLoadingRecent,
    isError: isLogsError,
  } = useQuery<PaginatedResponse<TokenUsageRecord>>({
    queryKey: ['token-usage-logs', '', 1, userEmail, sourceFilter],
    queryFn: async () => {
      let url = `${API_ROUTES.tokenUsage}?page=1&page_size=10`;
      if (userEmail) url += `&user__email__icontains=${encodeURIComponent(userEmail)}`;
      if (sourceFilter) url += `&source=${sourceFilter}`;
      const res = await api.get(url);
      return res.data;
    },
  });

  useEffect(() => {
    if (isStatsError || isLogsError) {
      toast.error('Error loading token usage data. Please try again.');
    }
  }, [isStatsError, isLogsError]);

  const openPanelHandler = (id: PanelId) => setOpenPanel(id);
  const closePanel = () => setOpenPanel(null);

  const statCards: Array<{
    key: keyof UsageStats;
    panelId: PanelId;
    label: string;
    icon: React.ReactNode;
    accentClass: string;
  }> = [
    {
      key: 'last_hour',
      panelId: 'last_hour',
      label: 'Last Hour',
      icon: <Clock size={14} />,
      accentClass: styles.accent_info,
    },
    {
      key: 'today',
      panelId: 'today',
      label: 'Today',
      icon: <Calendar size={14} />,
      accentClass: styles.accent_success,
    },
    {
      key: 'month',
      panelId: 'month',
      label: 'This Month',
      icon: <TrendingUp size={14} />,
      accentClass: styles.accent_warning,
    },
    {
      key: 'total',
      panelId: 'total',
      label: 'All Time',
      icon: <Database size={14} />,
      accentClass: styles.accent_primary,
    },
  ];

  return (
    <div className={styles.container}>
      {/* ── Filters ── */}
      <div className={styles.filter_bar}>
        <Select
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          options={userOptions}
          size="sm"
          searchable
        />
        <div className={styles.source_pills}>
          {SOURCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.source_pill} ${sourceFilter === opt.value ? styles.source_pill_active : ''}`}
              onClick={() => setSourceFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Top row: stat cards ── */}
      <div className={styles.cards_row}>
        {statCards.map(({ key, panelId, label, icon, accentClass }) => (
          <StatCard
            key={key}
            label={label}
            stat={stats?.[key]}
            isLoading={isLoadingStats}
            icon={icon}
            accentClass={accentClass}
            onClick={() => openPanelHandler(panelId)}
          />
        ))}
      </div>

      {/* ── Middle row: breakdown + summary + activity ── */}
      <div className={styles.middle_row}>
        {/* Left column: stacked cost breakdown + token summary */}
        <div className={styles.left_col}>
          <Card className={styles.breakdown_panel}>
            <div className={styles.panel_header}>
              <span className={styles.panel_title}>Cost Breakdown</span>
              <div className={styles.legend_inline}>
                <span className={styles.legend_claude}>
                  <span className={styles.legend_dot} />
                  Claude
                </span>
                <span className={styles.legend_gemini}>
                  <span className={styles.legend_dot} />
                  Gemini
                </span>
              </div>
            </div>
            <BreakdownChart
              stats={stats}
              isLoading={isLoadingStats}
              onBarClick={openPanelHandler}
            />
          </Card>

          {/* Token totals summary */}
          <Card className={styles.summary_panel}>
            <div className={styles.panel_header}>
              <span className={styles.panel_title}>Token Totals</span>
            </div>
            {isLoadingStats ? (
              <div className={styles.summary_grid}>
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className={styles.summary_cell}>
                    <Skeleton width="50px" height="18px" />
                    <Skeleton width="80px" height="10px" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.summary_grid}>
                <div className={styles.summary_cell}>
                  <span className={styles.summary_value}>
                    {(stats?.total?.claude_tokens ?? 0).toLocaleString()}
                  </span>
                  <span className={styles.summary_label}>Claude tokens</span>
                </div>
                <div className={styles.summary_cell}>
                  <span className={styles.summary_value}>
                    {(stats?.total?.gemini_tokens ?? 0).toLocaleString()}
                  </span>
                  <span className={styles.summary_label}>Gemini tokens</span>
                </div>
                <div className={styles.summary_cell}>
                  <span className={styles.summary_value}>
                    {(stats?.total?.total_tokens ?? 0).toLocaleString()}
                  </span>
                  <span className={styles.summary_label}>Total tokens</span>
                </div>
                <div className={styles.summary_cell}>
                  <span className={styles.summary_value}>{stats?.total?.count ?? 0}</span>
                  <span className={styles.summary_label}>Total API calls</span>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Right: recent activity */}
        <Card className={styles.activity_panel}>
          <div className={styles.panel_header}>
            <span className={styles.panel_title}>Recent Activity</span>
            <button className={styles.view_all_btn} onClick={() => openPanelHandler('all')}>
              View all
            </button>
          </div>
          <div className={styles.activity_list}>
            {isLoadingRecent ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={styles.activity_row}>
                  <Skeleton width="40px" />
                  <Skeleton width="45px" />
                  <Skeleton width="80px" />
                  <Skeleton width="45px" />
                  <Skeleton width="55px" />
                </div>
              ))
            ) : recentLogs?.results.length ? (
              recentLogs.results.map((log) => <ActivityRow key={log.id} log={log} />)
            ) : (
              <div className={styles.empty}>No activity yet.</div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Slide-over detail panel ── */}
      <SlideOver
        panelId={openPanel}
        onClose={closePanel}
        userEmail={userEmail}
        sourceFilter={sourceFilter}
      />
    </div>
  );
}
